import maplibregl from 'maplibre-gl';
import type { Scene, MapAdapter } from '../types';

const TITILER      = 'https://titiler-centinela-746476722093.us-central1.run.app';
const FLOW_COG     = 'gs://centinela-test-data/openclaw/flow/flow_field_rgba.tif';
const STREAM_COG   = 'gs://centinela-test-data/openclaw/flow/stream_order_cog.tif';
const DRYDOWN_COG  = 'gs://centinela-test-data/openclaw/flow/s2_drydown_slope_cog.tif';
const CSV_URL      = 'https://storage.googleapis.com/centinela-test-data/openclaw/sapi_agb_hydro_samples.csv';

type RiversLayerId = 'flow-acc' | 'stream-order' | 'drydown';

interface RiversLayerDef {
  id:      RiversLayerId;
  srcId:   string;
  lyrId:   string;
  label:   string;
  desc:    string;
  tileUrl: string;
  opacity: number;
  legend:  { color: string; label: string }[];
}

const LAYERS: RiversLayerDef[] = [
  {
    id:      'flow-acc',
    srcId:   'rivers-flow-acc-src',
    lyrId:   'rivers-flow-acc-layer',
    label:   'Flow Accumulation',
    desc:    'Log₁₀ drainage area from WhiteboxTools D-Infinity routing (COP-DEM 5 m). ' +
             'Bright channels carry the highest upstream accumulation \u2014 these are the landscape\u2019s ' +
             'natural drainage arteries, collecting and concentrating seasonal rainfall before it ' +
             'reaches the Zambezi.',
    tileUrl: `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
             `?url=${encodeURIComponent(FLOW_COG)}&bidx=3&rescale=0,255&colormap_name=blues&resampling=bilinear&nodata=0`,
    opacity: 0.85,
    legend: [
      { color: '#deebf7', label: 'Low accumulation'      },
      { color: '#6baed6', label: 'Secondary channels'    },
      { color: '#08306b', label: 'Main drainage network' },
    ],
  },
  {
    id:      'stream-order',
    srcId:   'rivers-stream-src',
    lyrId:   'rivers-stream-layer',
    label:   'Stream Order',
    desc:    'Strahler stream order from the 5 m COP-DEM. ' +
             'First-order headwaters feed second- and third-order channels that flow into the ' +
             'Sapi and Zambezi rivers. Higher-order channels respond more slowly to rainfall events, ' +
             'providing baseflow through the dry season.',
    tileUrl: `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
             `?url=${encodeURIComponent(STREAM_COG)}&bidx=1&rescale=1,6&colormap_name=blues&nodata=0&resampling=nearest`,
    opacity: 0.90,
    legend: [
      { color: '#c6dbef', label: 'Order 1 · headwater'  },
      { color: '#6baed6', label: 'Order 2'               },
      { color: '#08306b', label: 'Order 3+ · main stem'  },
    ],
  },
  {
    id:      'drydown',
    srcId:   'rivers-drydown-src',
    lyrId:   'rivers-drydown-layer',
    label:   'Drydown Slope',
    desc:    'S2 drydown slope — rate of surface moisture loss at dry-season onset (June). ' +
             'Blue pixels retain moisture longest (high pooling capacity); red pixels lose ' +
             'moisture fastest (exposed, low-retention terrain). These patterns identify ' +
             'intervention priority zones where small check structures can maximise moisture retention.',
    tileUrl: `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
             `?url=${encodeURIComponent(DRYDOWN_COG)}&bidx=1&rescale=-0.005,0.001&colormap_name=bwr_r`,
    opacity: 0.80,
    legend: [
      { color: '#2166ac', label: 'Slow dry · high retention' },
      { color: '#f7f7f7', label: 'Neutral'                   },
      { color: '#b2182b', label: 'Fast dry · low retention'  },
    ],
  },
];

// ── SVG histogram ────────────────────────────────────────────────────────────

function buildHistogram(values: number[]): SVGSVGElement {
  const BINS = 22;
  const W = 340, H = 170, PL = 36, PR = 14, PT = 12, PB = 36;
  const IW = W - PL - PR, IH = H - PT - PB;

  const vMin = Math.min(...values);
  const vMax = Math.max(...values);
  const binW  = (vMax - vMin) / BINS;

  const counts = new Array(BINS).fill(0);
  values.forEach(v => {
    const i = Math.min(BINS - 1, Math.floor((v - vMin) / binW));
    counts[i]++;
  });
  const maxCount = Math.max(...counts);

  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg') as SVGSVGElement;
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('width',  String(W));
  svg.setAttribute('height', String(H));
  (svg as unknown as { style: CSSStyleDeclaration }).style.overflow = 'visible';

  const el = <K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string>) => {
    const e = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v));
    return e;
  };

  // Y grid lines
  [0, 0.25, 0.5, 0.75, 1].forEach(f => {
    const y = PT + IH * (1 - f);
    svg.appendChild(el('line', { x1: String(PL), x2: String(W - PR), y1: String(y), y2: String(y), stroke: 'rgba(255,255,255,0.05)', 'stroke-width': '0.5' }));
    if (f > 0) {
      const t = el('text', { x: String(PL - 3), y: String(y), 'text-anchor': 'end', 'dominant-baseline': 'middle', 'font-size': '7', fill: '#6b8091' });
      t.textContent = Math.round(maxCount * f).toString();
      svg.appendChild(t);
    }
  });

  // Bars — colour driven by bin-centre value (not position) for semantic accuracy:
  //   centre ≤ −0.0005 (fast-dry zone): deep-brown palette
  //   −0.0005 to 0: transition amber/tan → neutral
  //   > 0: cyan/teal (moisture retention)
  const barW = IW / BINS;
  counts.forEach((count, i) => {
    if (count === 0) return;
    const x = PL + i * barW;
    const barH = (count / maxCount) * IH;
    const y = PT + IH - barH;
    const centre = vMin + (i + 0.5) * binW;
    let r: number, g: number, b: number;
    if (centre <= -0.0005) {
      // Deep brown → sienna (most negative = fastest dry)
      const t = Math.max(0, Math.min(1, (centre - vMin) / (-0.0005 - vMin)));
      r = Math.round(92  + (160 - 92)  * t);   // #5C2A00 → #A0522D
      g = Math.round(42  + (82  - 42)  * t);
      b = Math.round(0   + (45  - 0)   * t);
    } else if (centre <= 0) {
      // Sienna → neutral amber (transition zone)
      const t = centre / -0.0005;               // 1 at −0.0005, 0 at 0
      r = Math.round(210 + (160 - 210) * (1 - t));
      g = Math.round(160 + (82  - 160) * (1 - t));
      b = Math.round(80  + (45  - 80)  * (1 - t));
    } else {
      // Neutral → cyan (moisture retention)
      const t = Math.min(1, centre / (vMax || 0.001));
      r = Math.round(180 * (1 - t) + 50  * t);
      g = Math.round(180 * (1 - t) + 200 * t);
      b = Math.round(180 * (1 - t) + 240 * t);
    }
    svg.appendChild(el('rect', {
      x: String(x + 0.8), y: String(y),
      width:  String(barW - 1.6),
      height: String(barH),
      fill: `rgb(${r},${g},${b})`,
      opacity: '0.80',
    }));
  });

  // Zero line (if in range)
  const zeroFrac = (0 - vMin) / (vMax - vMin);
  if (zeroFrac > 0 && zeroFrac < 1) {
    const xz = PL + zeroFrac * IW;
    svg.appendChild(el('line', { x1: String(xz), x2: String(xz), y1: String(PT), y2: String(PT + IH), stroke: 'rgba(255,255,255,0.35)', 'stroke-width': '1', 'stroke-dasharray': '3 2' }));
    const zt = el('text', { x: String(xz + 2), y: String(PT + 8), 'font-size': '7', fill: 'rgba(255,255,255,0.4)' });
    zt.textContent = '0';
    svg.appendChild(zt);
  }

  // X axis ticks — show 5 evenly spaced labels in ×10⁻³
  [0, 0.25, 0.5, 0.75, 1].forEach(f => {
    const v = vMin + f * (vMax - vMin);
    const x = PL + f * IW;
    const t = el('text', { x: String(x), y: String(PT + IH + 10), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091' });
    t.textContent = (v * 1000).toFixed(1);
    svg.appendChild(t);
  });

  // Axis labels
  const xLbl = el('text', { x: String(PL + IW / 2), y: String(H - 2), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091' });
  xLbl.textContent = 'Drydown slope (×10⁻³ per day)';
  svg.appendChild(xLbl);

  const yLbl = el('text', { x: '8', y: String(PT + IH / 2), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091', transform: `rotate(-90, 8, ${PT + IH / 2})` });
  yLbl.textContent = 'Plot count';
  svg.appendChild(yLbl);

  // Annotation arrows — left = dry, right = wet retention
  const arrowY = PT + IH + 22;
  const lblLeft = el('text', { x: String(PL + 2), y: String(arrowY), 'font-size': '7', fill: '#C48B30', opacity: '0.85' });
  lblLeft.textContent = '← fast dry / low retention';
  svg.appendChild(lblLeft);

  const lblRight = el('text', { x: String(W - PR - 2), y: String(arrowY), 'text-anchor': 'end', 'font-size': '7', fill: '#7ecfef', opacity: '0.85' });
  lblRight.textContent = 'slow dry / high retention →';
  svg.appendChild(lblRight);

  return svg;
}

// ── SceneRivers ───────────────────────────────────────────────────────────────

export class SceneRivers implements Scene {
  readonly panel: HTMLElement;
  private mapLegend: HTMLElement | null = null;
  private activeId: RiversLayerId = 'flow-acc';
  private built = false;
  private chartBuilt = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly mapAdapter: MapAdapter,
  ) {
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'rivers';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Rivers & Pooling</div>
        <div class="sm-panel-sub">Flow Accumulation · D-Infinity · COP-DEM 5 m</div>
      </div>

      <div class="sm-headline">
        The drainage network is the landscape's memory — where water flows
        today determines where biomass grows tomorrow.
      </div>

      <div class="sm-panel-narrative">
        Derived from WhiteboxTools D-Infinity routing on the 5 m COP-DEM, the
        flow accumulation layer reveals Sapi's natural drainage arteries: the
        channels that collect seasonal rainfall and carry it downslope into
        lowland woodland and the Zambezi floodplain. These paths represent
        both the current hydrological baseline and the primary intervention
        targets — small check structures placed on high-accumulation channels
        extend the period of soil saturation into the dry season, directly
        expanding the moisture envelope that supports woody biomass growth.
      </div>

      <div class="sm-hydro-layers" style="padding:0 22px;">
        <div class="sm-hydro-layers-label">Map Layer</div>
        <div class="sm-hydro-layer-btns" id="sm-rivers-layer-btns">
          ${LAYERS.map(l => `
            <button class="sm-hydro-layer-btn${l.id === 'flow-acc' ? ' active' : ''}"
                    data-layer="${l.id}">${l.label}</button>`).join('')}
        </div>
        <div class="sm-hydro-layer-desc" id="sm-rivers-layer-desc"></div>
      </div>

      <div class="sm-hydro-mgmt-divider"></div>

      <div class="sm-rivers-hist-section">
        <div class="sm-hydro-mgmt-insight-title" style="padding:0 22px;margin-bottom:6px;">
          Dry-Season Moisture Transition
        </div>
        <div class="sm-hydro-mgmt-insight-body" style="padding:0 22px;margin-bottom:10px;">
          The S2 drydown slope measures how quickly each 10 m pixel loses its surface
          moisture signal as the wet season ends. Plots with slopes near zero retain
          moisture longer — a signature of topographic pooling, deep soils, and high
          canopy cover. These are exactly the positions that carry the highest
          above-ground biomass, and the positions most responsive to hydrological
          interventions that extend seasonal soil saturation.
        </div>
        <div id="sm-rivers-hist-wrap" style="padding:0 22px;">
          <div class="sm-hydro-loading" id="sm-rivers-hist-loading">
            <div class="sm-hydro-spinner"></div>
            <span class="sm-hydro-loading-text">Loading drydown data…</span>
          </div>
        </div>
      </div>`;

    root.appendChild(this.panel);

    this.mapLegend = document.createElement('div');
    this.mapLegend.className = 'sm-map-layer-legend';
    this.mapLegend.style.display = 'none';
    document.getElementById('sm-map')?.appendChild(this.mapLegend);
  }

  onEnter(): void {
    if (!this.built) {
      const btns = this.panel.querySelector<HTMLElement>('#sm-rivers-layer-btns')!;
      btns.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.sm-hydro-layer-btn');
        if (!btn) return;
        btns.querySelectorAll('.sm-hydro-layer-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.showLayer(btn.dataset['layer'] as RiversLayerId);
      });
      this.built = true;
    }

    if (!this.chartBuilt) {
      this.chartBuilt = true;
      this.buildHistogram();
    }

    this.showLayer(this.activeId);
  }

  onLeave(): void {
    const map = this.rawMap();
    LAYERS.forEach(l => {
      if (map.getLayer(l.lyrId)) map.removeLayer(l.lyrId);
      if (map.getSource(l.srcId)) map.removeSource(l.srcId);
    });
    if (this.mapLegend) this.mapLegend.style.display = 'none';
  }

  private showLayer(id: RiversLayerId): void {
    this.activeId = id;
    const map = this.rawMap();

    LAYERS.forEach(l => {
      if (map.getLayer(l.lyrId)) map.removeLayer(l.lyrId);
      if (map.getSource(l.srcId)) map.removeSource(l.srcId);
    });

    const def = LAYERS.find(l => l.id === id)!;
    map.addSource(def.srcId, { type: 'raster', tiles: [def.tileUrl], tileSize: 256 });
    const firstSymbol = map.getStyle().layers?.find(l => l.type === 'symbol')?.id;
    map.addLayer(
      { id: def.lyrId, type: 'raster', source: def.srcId, paint: { 'raster-opacity': def.opacity } },
      firstSymbol,
    );

    const descEl = this.panel.querySelector<HTMLElement>('#sm-rivers-layer-desc')!;
    descEl.textContent = def.desc;

    if (this.mapLegend) {
      this.mapLegend.innerHTML =
        `<div class="sm-map-legend-title">${def.label}</div>` +
        def.legend.map(s =>
          `<div class="sm-map-legend-row">
            <span class="sm-map-legend-swatch" style="background:${s.color};border:1px solid rgba(255,255,255,0.12)"></span>
            <span class="sm-map-legend-label">${s.label}</span>
          </div>`
        ).join('');
      this.mapLegend.style.display = 'block';
    }
  }

  private async buildHistogram(): Promise<void> {
    const wrap      = this.panel.querySelector<HTMLElement>('#sm-rivers-hist-wrap')!;
    const loadingEl = this.panel.querySelector<HTMLElement>('#sm-rivers-hist-loading')!;

    try {
      const res  = await fetch(CSV_URL);
      const text = await res.text();
      const lines = text.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const idx = headers.indexOf('s2_drydown_slope');
      if (idx < 0) { loadingEl.style.display = 'none'; return; }

      const values = lines.slice(1)
        .map(l => parseFloat(l.split(',')[idx]))
        .filter(v => isFinite(v));

      loadingEl.style.display = 'none';
      wrap.appendChild(buildHistogram(values));
    } catch (e) {
      loadingEl.style.display = 'none';
      console.error('[SceneRivers] histogram fetch failed', e);
    }
  }

  private rawMap(): maplibregl.Map {
    return (this.mapAdapter as unknown as { map: maplibregl.Map }).map;
  }
}
